import { Button } from "@msuite/ui/button";
import { Divider } from "@msuite/ui/divider";
import { FormCheckbox, NumberInput, Select } from "@msuite/ui/form";
import { resolver } from "@msuite/ui/libs/form";
import { Loading } from "@msuite/ui/loading-screen";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import { responseHandler } from "@msuite/ui/utils";
import {
	getVacationDaysAccrualTypeTranslationKey,
	vacationDaysAccrualTypes,
} from "@msuite/utils/absences";
import { moment } from "@msuite/utils/date";
import { guardNumber } from "@msuite/utils/numbers";
import { type AbsenceGeneralFormSchema, absenceGeneralFormSchema } from "@msuite/utils/schemas";
import { Fragment } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { client } from "@/hono";

import { HalfDaysField } from "./half-days-field";

export function AbsenceGeneralForm() {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm({
		resolver: resolver(absenceGeneralFormSchema),
		defaultValues: async () => {
			const response = await client.organizations.absences.settings.$get();
			const data = await responseHandler(response);
			return data.absenceSettings;
		},
	});
	const [vacationDaysExpire, vacationDaysExpireAtMonth] = useWatch({
		control: methods.control,
		name: ["vacationDaysExpire", "vacationDaysExpireAtMonth"],
	});
	/** Functions */
	async function handleOnSubmit(json: AbsenceGeneralFormSchema) {
		const response = await client.organizations.absences.$patch({ json });
		await responseHandler(response);
		toast.success(t("general.successfully_updated"));
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<form
				className="grid grid-cols-2 gap-4"
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Loading isLoading={methods.formState.isLoading}>
					<NumberInput
						label={t("absences.default_vacation_days")}
						isRequired
						control={methods.control}
						name="vacationDays"
						min={0}
						max={365}
					/>
					<Select
						isRequired
						isNullable={false}
						label={t("absences.accrual_type.accrual_type")}
						control={methods.control}
						name="vacationDaysAccrualType"
						options={vacationDaysAccrualTypes.map((accrualType) => ({
							label: t(getVacationDaysAccrualTypeTranslationKey(accrualType)),
							value: accrualType,
							item: { accrualType },
						}))}
					/>
					<Divider
						className="col-span-2"
						variant="card"
					/>
					<div className="col-span-2">
						<FormCheckbox
							control={methods.control}
							name="vacationDaysExpire"
						>
							{t("absences.vacation_days_expire")}
						</FormCheckbox>
					</div>
					{vacationDaysExpire && (
						<Fragment>
							<Select
								label={t("dates.day")}
								control={methods.control}
								name="vacationDaysExpireAtDay"
								isNullable={false}
								defaultValue={31}
								isRequired
								options={Array.from({
									length: moment()
										.month(guardNumber(guardNumber(vacationDaysExpireAtMonth) - 1))
										.daysInMonth(),
								}).map((_, i) => ({
									label: (i + 1).toString().padStart(2, "0"),
									value: i + 1,
									item: { day: i + 1 },
								}))}
							/>
							<Select
								label={t("dates.month")}
								control={methods.control}
								name="vacationDaysExpireAtMonth"
								isNullable={false}
								defaultValue={3}
								isRequired
								options={Array.from({
									length: 12,
								}).map((_, i) => ({
									label: moment().startOf("year").add(i, "month").format("MMMM"),
									value: i + 1,
									item: { month: i + 1 },
								}))}
							/>
						</Fragment>
					)}
					<Divider
						className="col-span-2"
						variant="card"
					/>
					<HalfDaysField />
					<Divider
						variant="card"
						className="col-span-2"
					/>
					<div className="h-stack justify-end col-span-2">
						<Button
							type="submit"
							variant="primary"
							isLoading={methods.formState.isSubmitting}
						>
							{t("general.submit")}
						</Button>
					</div>
				</Loading>
			</form>
		</FormProvider>
	);
}
